import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import NewClientIntakeForm from '@/components/meal-planning/auto_generated/NewClientIntakeForm';
import { useClientProfile } from '@/hooks/useClientProfile';
import { generateMealPlanWithOverrides, getClientById } from '@/lib/apiService';
import FeedbackButton from '@/components/feedback/FeedbackButton';
import { FeedbackContext } from '@/components/feedback/FeedbackPanel';
import CreateMealPlanHeader from '@/components/meal-planning/CreateMealPlanHeader';
import { MEAL_PLAN_MAX_DAYS } from '@/components/meal-planning/MealPlanScheduleFields';
import { showErrorToast, showWarningToast } from '@/utils/toast-utils';
import {
  extractMealPlanDateConflicts,
  formatMealPlanDateConflictMessage,
} from '@/utils/meal-plan-date-conflicts';

interface MealPlanApiPayload {
  type: string;
  action: string;
  clientId: string;
  name?: string;
  startDate: string;
  days: number;
  overrideClientGoals?: {
    eerGoalCalories: number;
    proteinGoalMin: number;
    proteinGoalMax: number;
    carbsGoalMin: number;
    carbsGoalMax: number;
    fatGoalMin: number;
    fatGoalMax: number;
  };
  overrideMealProgram?: {
    meals: Array<{
      mealOrder: number;
      mealName: string;
      mealTime: string;
      targetCalories?: number;
      mealType: string;
    }>;
  };
  allergies: string[];
  intolerances?: string[];
  cuisineTypes: string[];
  preferences: string[];
  dietaryPreferences?: string[];
}

const CreateAutoMealPlanPage = () => {
  const { loading: profileLoading } = useClientProfile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedClientId = searchParams.get('clientId') || '';
  const requestedPlanName = searchParams.get('name')?.trim() || '';
  const requestedDaysRaw = Number(searchParams.get('days'));
  const requestedDays =
    Number.isFinite(requestedDaysRaw) && requestedDaysRaw > 0
      ? Math.min(MEAL_PLAN_MAX_DAYS, Math.max(1, Math.floor(requestedDaysRaw)))
      : MEAL_PLAN_MAX_DAYS;
  const today = React.useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultEndDate = React.useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + (requestedDays - 1));
    return date.toISOString().slice(0, 10);
  }, [requestedDays]);
  const [loading, setLoading] = useState(false);
  const [isLoadingClient, setIsLoadingClient] = useState(!!preSelectedClientId);
  const [preSelectedClientData, setPreSelectedClientData] = useState<any>(null);
  const [dateConflictMessage, setDateConflictMessage] = useState<string | null>(null);

  // Load pre-selected client info
  useEffect(() => {
    if (preSelectedClientId) {
      const loadClient = async () => {
        setIsLoadingClient(true);
        try {
          const response = await getClientById(preSelectedClientId);
          if (response.client) {
            setPreSelectedClientData({
              clientId: preSelectedClientId,
              planName: requestedPlanName,
              startDate: today,
              endDate: defaultEndDate,
            });
          
          }
        } catch (error) {
          console.error('Error loading pre-selected client:', error);
          showErrorToast(error, "Error Loading Client");
        } finally {
          setIsLoadingClient(false);
        }
      };
      loadClient();
    }
  }, [preSelectedClientId, requestedPlanName, today, defaultEndDate]);

  const handleFormSubmit = async (data: MealPlanApiPayload) => {
    setLoading(true);
    setDateConflictMessage(null);

    try {
      console.log('Received API payload:', data);

      // Transform the payload to include endDate for the API
      const apiPayload = {
        ...data,
        endDate: new Date(new Date(data.startDate).getTime() + (data.days - 1) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
      };

      const response = await generateMealPlanWithOverrides(apiPayload);

      // Support both shapes: top-level draftId (spec) or response.data.draftId
      const draftId = (response as any)?.draftId ?? (response as any)?.data?.draftId;
      const planName = data.name?.trim() || undefined;
      if (draftId) {
        navigate(`/automated-meal-plan/draft/${draftId}/client/${data.clientId}`, {
          state: planName ? { planName } : undefined,
        });
      } else if (response && (response as any).success && (response as any).data) {
        const altId = (response as any).data.draftId;
        if (altId) {
          navigate(`/automated-meal-plan/draft/${altId}/client/${data.clientId}`, {
            state: planName ? { planName } : undefined,
          });
        }
      }
    } catch (error) {
      console.error('Error generating auto meal plan:', error);
      const dateConflicts = extractMealPlanDateConflicts(error);
      if (dateConflicts.length > 0) {
        const message = formatMealPlanDateConflictMessage(dateConflicts);
        setDateConflictMessage(message);
        showWarningToast('Date conflict', message);
        return;
      }
      showErrorToast(error, "Generation Failed");
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className="space-y-6 p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7">
      <CreateMealPlanHeader
        title="Auto-Generated Meal Plans"
        description="Automatically generate complete meal plans based on client dietary goals and preferences."
        actions={
          <FeedbackButton
            clientId={preSelectedClientId || undefined}
            label="Testing Feedback"
            defaultContext={FeedbackContext.AUTO_MEAL_PLAN_GENERATION}
            title="Testing Feedback – Auto-Generated Meal Plans"
            description="Please provide feedback on the Auto-Generated Meal Plan creation process, including client selection, meal plan generation, and overall workflow. Please specify the date of feedback. Multiple feedback entries allowed."
            titlePlaceholder="e.g., Auto meal plan generation issues"
            detailsPlaceholder="Describe the steps, date of feedback, and observations when creating an auto-generated meal plan."
            submitLabel="Save Feedback"
            successDescription="Thanks for helping us improve Auto-Generated Meal Planning."
          />
        }
      />

      {isLoadingClient ? (
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
            <span className="text-lg text-muted-foreground">Loading client data...</span>
          </CardContent>
        </Card>
      ) : (
        <NewClientIntakeForm
          onSubmit={handleFormSubmit}
          loading={loading || profileLoading}
          dateConflictMessage={dateConflictMessage}
          initialData={
            preSelectedClientData ||
            {
              ...(preSelectedClientId ? { clientId: preSelectedClientId } : {}),
              ...(requestedPlanName ? { planName: requestedPlanName } : {}),
              startDate: today,
              endDate: defaultEndDate,
            }
          }
        />
      )}
      
    </div>
      </Paper>
    </Box>
  );
};

export default CreateAutoMealPlanPage;
